import { routes } from './routes';

/**
 * V1.5.M10 B-P2 — cross-subdomain Google sign-in.
 *
 * Google validates the OAuth JavaScript origin against a per-origin allowlist
 * with NO wildcard support, so a brand-new <client>.2connect.ai cannot run the
 * Google popup. Instead every white-label subdomain relays its Google login
 * through the ONE canonical origin (already allowlisted), which hands the
 * session back via a single-use code (see backend auth-relay-code.entity.ts).
 *
 * This module holds the small, shared pieces of that dance so the rule lives in
 * exactly one place.
 */

/** The single origin where Google sign-in is authorized. */
export const CANONICAL_ORIGIN = 'https://app.2connect.ai';

/** Single-label https subdomain of 2connect.ai (acme.2connect.ai, ...).
 *  Anchored both ends: evil-2connect.ai.attacker.com and a.b.2connect.ai
 *  do NOT match. Mirrors the backend CORS predicate. */
const TWOCONNECT_SUBDOMAIN = /^https:\/\/[a-z0-9-]+\.2connect\.ai$/;

/** A return origin is safe to hand a session back to iff it is a 2connect.ai
 *  subdomain. This is the open-redirect guard: the canonical origin will only
 *  ever bounce a freshly-authenticated session to one of our own subdomains. */
export const isAllowedReturnOrigin = (
  origin: string | null | undefined,
): origin is string => !!origin && TWOCONNECT_SUBDOMAIN.test(origin);

/**
 * True when the current page must relay its Google login through the canonical
 * origin: it is a 2connect.ai subdomain OTHER than the canonical one. The
 * canonical origin itself, localhost, and preview hosts return false (they run
 * the Google popup directly, exactly as before).
 */
export const needsOauthRelay = (): boolean => {
  const origin = window.location.origin;
  return origin !== CANONICAL_ORIGIN && TWOCONNECT_SUBDOMAIN.test(origin);
};

/**
 * When the current page is the canonical launch interstitial, the validated
 * return origin it should relay back to (else null). Used by the Google
 * onSuccess handler to decide between relay-back and normal navigation.
 */
export const getLaunchReturnOrigin = (): string | null => {
  if (window.location.pathname !== routes.oauthLaunch) return null;
  const ret = new URLSearchParams(window.location.search).get('return');
  return isAllowedReturnOrigin(ret) ? ret : null;
};
